import { globalGetService, globalPostService } from '../../../utils/globalApiServices';
import axiosInstance from '../../../utils/apiInterceptor';
import { checkApiStatus } from '../../../utils';

// Creates the upload session for a data room folder; the response carries the session (upload_id)
export const createUploadSessionApi = ({ assetType, assetSlug, folderUuid, totalFiles, totalBytes }) =>
  globalPostService(`records/v4/${assetType}/${assetSlug}/data-room/uploads/`, {
    folder_uuid: folderUuid,
    total_files: totalFiles,
    total_bytes: totalBytes,
  });

// Normalises the session list ({ count, page, limit, results }) into the { list, pagination }
// shape the shared Pagination component expects
export const getUploadSessionsApi = ({ page = 1, per_page = 20 } = {}) =>
  globalGetService('records/v4/data-room/uploads/', { page, limit: per_page })
    .then(response => {
      if (!checkApiStatus(response)) {
        return { ok: false, message: response.data ? response.data.message : '', list: [], pagination: null };
      }
      const data = response.data.data || {};
      return {
        ok: true,
        list: data.results || [],
        pagination: { page: data.page || page, per_page: data.limit || per_page, total: data.count || 0 },
      };
    });

// The engine needs failures to reject (the global*Service helpers never settle on network errors),
// so these call the shared axios instance directly and keep its auth header/interceptors
const uploadFilesUrl = (assetType, assetSlug, uploadId) => `records/v4/${assetType}/${assetSlug}/data-room/uploads/${uploadId}/files/`;

const unwrap = (response) => {
  if (!checkApiStatus(response)) {
    throw new Error((response && response.data && response.data.message) || 'Request failed');
  }
  return response.data.data || {};
};

// rejected / skipped entries may be plain strings or objects with a reason
const describe = (entry, idKeys) => {
  if (typeof entry === 'string') return { id: entry, reason: null };
  const key = idKeys.find(k => entry && entry[k]);
  return { id: key ? entry[key] : null, reason: entry ? entry.reason || entry.error || entry.message || null : null };
};

/**
 * POST .../uploads/{uploadId}/files/  { files: [{ name, size, type, path }] }
 * Resolves { files: [{ fileId, path, name, status }], rejected: [{ id, reason }] }
 * where a rejected id is the file's path (or name).
 */
export const registerUploadFilesApi = ({ assetType, assetSlug, uploadId, files }) =>
  axiosInstance.post(uploadFilesUrl(assetType, assetSlug, uploadId), { files })
    .then(unwrap)
    .then(data => ({
      files: (data.files || []).map(file => ({ fileId: file.file_id, path: file.path, name: file.name, status: file.status })),
      rejected: (data.rejected || []).map(entry => describe(entry, ['path', 'name'])),
    }));

/**
 * POST .../uploads/{uploadId}/files/urls/  { fileIds: [...] }
 * Resolves { urls: { [fileId]: { url, method, headers, expiresAt } }, skipped: [{ id, reason }] }.
 * The URL signs content-type, so the returned headers must be sent unchanged on the PUT.
 */
export const getUploadFileUrlsApi = ({ assetType, assetSlug, uploadId, fileIds }) =>
  axiosInstance.post(`${uploadFilesUrl(assetType, assetSlug, uploadId)}urls/`, { fileIds })
    .then(unwrap)
    .then(data => {
      const urls = {};
      (data.urls || []).forEach(item => {
        if (item.file_id && item.signed_url) {
          urls[item.file_id] = {
            url: item.signed_url,
            method: item.method || 'PUT',
            headers: item.headers || null,
            expiresAt: item.expires_at ? Date.parse(item.expires_at) : null,
          };
        }
      });
      return { urls, skipped: (data.skipped || []).map(entry => describe(entry, ['file_id', 'fileId', 'id'])) };
    });

/**
 * PUT .../uploads/{uploadId}/files/{fileId}/
 * Tells the server a file's final state after the direct-to-storage upload:
 * { status: 'COMPLETED', uploaded_bytes } or { status: 'FAILED', uploaded_bytes, error_message }
 */
export const updateUploadFileStatusApi = ({ assetType, assetSlug, uploadId, fileId, status, uploadedBytes, errorMessage }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, {
    status,
    uploaded_bytes: uploadedBytes,
    ...(errorMessage ? { error_message: errorMessage } : {}),
  }).then(unwrap);
